import React from "react";
import { BookOpen, Calendar, Clock } from "lucide-react";
import { styles as baseStyles } from "../../style/DoctorProfile.styles";

/**
 * ServicesAndBooking
 *
 * Props:
 * - services: Array<{ id?: string|number, title: string, price?: string, duration?: string, description?: string }>
 * - onBook: (service) => void
 * - canBook?: boolean                     // ⬅️ NEW: only patients true
 * - styles?: typeof baseStyles
 * - blockedHint?: string                  // optional: UI hint when blocked
 */
/**
 * Props (new):
 * - canBook?: boolean
 * - showRegisterPatient?: boolean
 * - onRegisterPatient?: () => void
 */
const ServicesAndBooking = ({
  services = [],
  onBook,
  canBook = false,
  showRegisterPatient = false,
  onRegisterPatient,
  styles = baseStyles,
}) => {
  const handleBook = (service) => {
    if (!canBook) return;
    onBook?.(service);
  };

  return (
    <div style={styles.section}>
      <div style={styles.sectionHeader}>
        <BookOpen style={{ width: 20, height: 20, color: "#10b981" }} />
        <h2 style={styles.sectionTitle}>Medical Services &amp; Booking</h2>
      </div>

      {/* Inline CTA when role is null */}
      {showRegisterPatient && (
        <div style={{
          margin: "0 0 12px",
          padding: 12,
          background: "#ECFDF5",
          border: "1px solid #A7F3D0",
          borderRadius: 10,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10
        }}>
          <span style={{ color: "#065F46", fontSize: 13 }}>
            You don’t have a role yet. Register as a patient to enable booking.
          </span>
          <button
            type="button"
            onClick={onRegisterPatient}
            style={{
              padding: "8px 12px",
              borderRadius: 8,
              border: "none",
              fontWeight: 700,
              color: "white",
              background: "linear-gradient(135deg, #10B981, #059669)",
              cursor: "pointer"
            }}
          >
            Register as Patient
          </button>
        </div>
      )}

      <div style={styles.sectionContent}>
        {services.length > 0 ? (
          <>
            {services.map((service, index) => (
              <div
                key={service.id ?? index}
                style={{ ...styles.serviceCard, position: "relative", transition: "all 0.3s ease" }}
                className="service-card"
              >
                <div style={styles.serviceHeader}>
                  <h3 style={styles.serviceTitle}>{service.title}</h3>

                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    {service.price && <span style={styles.servicePrice}>{service.price}</span>}

                    <button
                      type="button"
                      onClick={() => handleBook({
                        id: service.id ?? index,
                        title: service.title,
                        price: service.price,
                        duration: service.duration,
                        description: service.description,
                      })}
                      disabled={!canBook}
                      aria-disabled={!canBook}
                      title={!canBook ? (showRegisterPatient
                        ? "Register as a patient to book"
                        : "Only patient accounts can book") : "Book appointment"}
                      style={{
                        padding: "8px 16px",
                        background: "linear-gradient(135deg, #3b82f6, #6366f1)",
                        color: "white",
                        border: "none",
                        borderRadius: 8,
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: canBook ? "pointer" : "not-allowed",
                        opacity: canBook ? 1 : 0.65,
                        transition: "all 0.2s ease",
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                      onMouseEnter={(e) => {
                        if (!canBook) return;
                        e.currentTarget.style.background = "linear-gradient(135deg, #1d4ed8, #4338ca)";
                        e.currentTarget.style.transform = "translateY(-1px)";
                      }}
                      onMouseLeave={(e) => {
                        if (!canBook) return;
                        e.currentTarget.style.background = "linear-gradient(135deg, #3b82f6, #6366f1)";
                        e.currentTarget.style.transform = "translateY(0)";
                      }}
                    >
                      <Calendar style={{ width: 12, height: 12 }} />
                      Book This
                    </button>
                  </div>
                </div>

                {service.description && (
                  <p style={styles.serviceDescription}>{service.description}</p>
                )}

                {service.duration && (
                  <div style={styles.serviceMeta}>
                    <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <Clock style={{ width: 12, height: 12 }} />
                      Duration: {service.duration}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </>
        ) : (
          <div style={styles.emptyState}>
            <BookOpen style={{ width: 48, height: 48, margin: "0 auto 16px auto" }} />
            <p>No services listed</p>
            <p style={{ fontSize: 12, color: "#9ca3af", marginTop: 8 }}>
              Contact the doctor directly to inquire about available services
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ServicesAndBooking;
